import * as React from 'react';
import { Tree as TreeNative } from '../ui-native/Tree';
import { observer } from 'mobx-react';
import { IReactionDisposer, makeObservable, observable, reaction } from 'mobx';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { cs } from '@shared/utils/utils-classname';
import { Button } from '@shared/ui-react/Button';


export interface ITreeCustomizeOptions {
    show?: boolean;
    filterOnly?: boolean;
    filterTreePlaceholder?: string;
    showNavigation?: boolean;
    hideExpandOptions?: boolean;
    elementSelector?: string;
}

@observer
export class TreeToolbar extends React.Component<Partial<{
    tree: TreeNative
} & ITreeCustomizeOptions>> {
    @observable.ref private tree: TreeNative = null;
    @observable filterText = '';

    private filterInputEl: HTMLInputElement = null;
    private filterAutorunDispose: IReactionDisposer;
    private initialLoad: boolean = true;
    private dropdown: Dropdown = null;

    private expandOptions = Object.entries({
        none: kuura.getMessage('general.tree.on_start_expand.none'),
        firstLevel: kuura.getMessage('general.tree.on_start_expand.first_level'),
        all: kuura.getMessage('general.tree.on_start_expand.all'),
        restoreState: kuura.getMessage('general.tree.on_start_expand.restore')
    });

    static defaultProps = {
        showNavigation: false
    }

    constructor(
        props: Partial<{
            tree: TreeNative
        } & ITreeCustomizeOptions>
    ) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        if (this.props.tree) {
            this.initComponent(this.props.tree);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.tree !== prevProps.tree) {
            this.initComponent(this.props.tree);
        }
    }

    componentWillUnmount(): void {
        if (this.filterAutorunDispose) {
            this.filterAutorunDispose();
        }
    }

    render() {
        const isRootSelected = this.tree?.getSelected()?.isRoot();
        const hasRecent = this.tree?.recentlySelected.length > 1;

        if (!this.tree) return null;

        const OtherCtl = <div className='btn-group' style={{ boxShadow: 'none' }}>
            {this.props.showNavigation &&<>
                <Dropdown className='ik-button-small ik-button-secondary--subtle file-tree__settings__dropdown-trigger' tooltip={kuura.getMessage('general.tree.recently_selected')}
                          closeOnBodyClick={true} lazyRender={false} disabled={!hasRecent}
                          getBody={() => {
                              const recent = (this.props.tree?.recentlySelected || [])?.slice(1);
                              if (!recent.length) return <div className="text-center padded-half">{__('general.empty')}</div>
                              return <ul className='clickable'>{this.props.tree?.recentlySelected?.slice(1).map((r, i) => <li key={i} onClick={() => this.props.tree.selectByUuid(r.uuid)}>{r.label}</li>)}</ul>
                          }}>
                    <i className='iki iki-history'></i>
                </Dropdown>
                <Button className='ik-button-small ik-button-secondary--subtle' disabled={!hasRecent} icon='iki-chevron-left' tooltip={__('general.back')} onClick={() => this.tree.goBack()} style={{ paddingLeft: 4, paddingRight: 3, fontSize: 12 }}/>
                <Button className='ik-button-small ik-button-secondary--subtle' disabled={!hasRecent} icon='iki-chevron-right' tooltip={__('general.forward')} onClick={() => this.tree.goForward()} style={{ paddingLeft: 4, paddingRight: 3, fontSize: 12 }}/>
                <button className='ik-button-small ik-button-secondary--subtle'
                        onClick={this.goUp} disabled={isRootSelected}
                        style={{ paddingLeft: 4, paddingRight: 3 }}>
                            <i className='iki iki-chevron-up'></i>
                        </button>
            </>}
            <Button className='file-tree__toolbar__expand-all ik-button-small ik-button-secondary--subtle'
                    tooltip={kuura.getMessage('general.tree.expand_tree')}
                    icon='iki-list-expand'
                    onClick={this.expandAll}/>

            <Button className='file-tree__toolbar__collapse-all ik-button-small ik-button-secondary--subtle'
                    tooltip={kuura.getMessage('general.tree.collapse_tree')}
                    icon='iki-list-collapse'
                    onClick={this.collapseAll}/>

            <Dropdown className='ik-button-small ik-button-secondary--subtle file-tree__settings__dropdown-trigger' tooltip={kuura.getMessage('general.tree.tree_settings')}
                      closeOnBodyClick={false} ref={el => this.dropdown = el}>
                <i className='iki iki-tree-settings'/>
                <ul>
                    {this.tree.fetchContentCountsFn && <>
                        {this.tree.hideEmptyFoldersAlways ?
                            <li><i className="iki iki-hide"/>&nbsp;{kuura.getMessage('general.empty_folders_are_hidden')}</li>
                            :
                            <li onClick={this.tree.toggleHideEmptyFolders} className='file-tree__settings__hide-empty-folders clickable'>
                                <input type='checkbox' checked={this.tree.hideEmptyFolders} readOnly/> {kuura.getMessage('general.hide_empty_folders')}
                            </li>
                        }
                    </>}

                    {!this.props.hideExpandOptions && <li>
                        <div className='flex-column' style={{ padding: 0 }}>
                            <div style={{ marginBottom: '0.5em' }}>{kuura.getMessage('general.tree.on_start_expand')}:</div>

                            {this.expandOptions.map(([key, name]) => (
                                <label className={cs('checkbox-label', 'file-tree__settings__auto-expand-' + key)} key={key}>
                                    <input type='radio'
                                           checked={this.tree.expandOnStart === key}
                                           onChange={() => this.tree.setExpandOnStart(key as any)}/>
                                    <span>{name}</span>
                                </label>
                            ))}
                        </div>
                    </li>}

                    {(this.tree?.showContextMenuCreateFolder && !window.location.href.includes('mobile')) && <li className='clickable' onClick={() => {
                        this.tree.openCreateFolder(this.tree.getSelected() ?? this.tree.root)
                        this.dropdown.close();
                    }}><i className="iki iki-folder-closed"/>&nbsp;{__('js_files.create_folder')}</li>}
                </ul>
            </Dropdown>
        </div>;

        return (
            <header className={cs('flex-row', 'centered', { 'fade-disable': !this.tree })}>
                <input ref={el => this.filterInputEl = el}
                       type='text'
                       placeholder={this.props.filterTreePlaceholder || __('general.tree.filter_tree')}
                       style={{  marginLeft: 2 }}
                       onChange={e => this.filterText = e.currentTarget.value}
                       onKeyDown={this.handleFilterKeyboard}
                       onBlur={this.handleFilterBlur}
                       value={this.filterText}
                       className='file-tree__toolbar__text-filter'
                />

                { !this.props.filterOnly && OtherCtl }
            </header>
        );
    }

    private goUp = () => {
        if (this.props.tree.getSelected().parent) {
            this.props.tree.getSelected().parent.setSelected()
        }
    }

    private initComponent = (tree: TreeNative) => {
        this.tree = tree;
        if (tree.isRendered) {
            this.initTree(tree);
        }
        else {
            tree.on('render', () => this.initTree(tree));
        }
    };

    private initTree = (tree: TreeNative) => {
        if (!this.initialLoad) return;

        // filter
        if (this.filterAutorunDispose) this.filterAutorunDispose();
        this.filterAutorunDispose = reaction(() => this.filterText, text => {
            text = this.filterText.trim().toLowerCase();
            if (tree) {
                tree.filterByNodeText(text);
            }
        }, { delay: 50 });

        this.initialLoad = false;
    };

    private expandAll = () => {
        if (this.tree) {
            this.tree.expandAll();
        }
    };

    private collapseAll = () => {
        if (this.tree) {
            this.tree.collapseAll();
        }
    };

    public focusInput = () => {
        if (this.filterInputEl) {
            setTimeout(() => this.filterInputEl.focus(), 50);
        }
    };

    private handleFilterBlur = e => {
        this.tree.unHighlightHighlighted();
    }

    private handleFilterKeyboard = e => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.tree.highlightNext();
        }
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.tree.highlightPrevious();
        }
        else if (e.key === 'Escape') {
            this.filterText = '';
            this.focusInput();
            this.tree.unHighlightHighlighted();
        }
        else if (e.key === 'Enter') {
            this.tree.selectHighlighted();
        }
    };
}
